import {
  useAudioControls,
  type AudioTimelineSegment,
} from '@/lib/audio/audio-player-provider';
import type { TranscriptionToken } from '@/lib/transcription/types';
import { convexQuery } from '@convex-dev/react-query';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';

interface UsePlaybackDataOptions {
  reportId: Id<'reports'>;
  enabled: boolean;
}

/**
 * Fetches playback segments, loads token files for each segment,
 * and manages the audio timeline lifecycle.
 */
export const usePlaybackData = ({
  reportId,
  enabled,
}: UsePlaybackDataOptions) => {
  const appliedTimelineSignatureRef = useRef<string | null>(null);
  const loadedTokensSignatureRef = useRef<string | null>(null);
  const [remoteTokens, setRemoteTokens] = useState<TranscriptionToken[] | null>(
    null,
  );
  const [isTokensLoading, setIsTokensLoading] = useState(false);
  const [tokensError, setTokensError] = useState<string | null>(null);

  const { setAudioTimeline } = useAudioControls();

  const { data: playback } = useQuery(
    convexQuery(api.recording.getPlaybackData, enabled ? { reportId } : 'skip'),
  );

  const playbackTimeline = useMemo<AudioTimelineSegment[]>(() => {
    return (playback?.playbackTimeline ?? []).map((segment) => ({
      url: segment.audioUrl,
      startMs: segment.startMs,
      endMs: segment.endMs,
      durationMs: segment.durationMs,
      seq: segment.seq,
    }));
  }, [playback?.playbackTimeline]);

  const playbackTimelineSignature = useMemo(
    () =>
      playbackTimeline
        .map(
          (segment) =>
            `${segment.seq}|${segment.startMs}|${segment.endMs ?? ''}|${segment.durationMs ?? ''}`,
        )
        .join(','),
    [playbackTimeline],
  );

  const shouldLoadTokens =
    enabled && (playback?.playbackTimeline?.length ?? 0) > 0;

  const tokensDatasetSignature = useMemo(() => {
    if (!shouldLoadTokens) return null;

    return (playback?.playbackTimeline ?? [])
      .map(
        (segment) => `${segment.seq}|${segment.startMs}|${segment.tokensUrl}`,
      )
      .join(',');
  }, [playback?.playbackTimeline, shouldLoadTokens]);

  useEffect(() => {
    if (!shouldLoadTokens || !tokensDatasetSignature) {
      setRemoteTokens(null);
      setTokensError(null);
      setIsTokensLoading(false);
      loadedTokensSignatureRef.current = null;
      return;
    }

    if (loadedTokensSignatureRef.current === tokensDatasetSignature) {
      setIsTokensLoading(false);
      return;
    }

    let cancelled = false;
    setIsTokensLoading(true);
    setTokensError(null);

    Promise.all(
      (playback?.playbackTimeline ?? []).map(async (segment) => {
        const response = await fetch(segment.tokensUrl);
        if (!response.ok) {
          throw new Error(
            `Failed to load segment tokens: ${response.statusText}`,
          );
        }

        const data = await response.json();
        const tokens = Array.isArray(data) ? data : data?.tokens;
        if (!Array.isArray(tokens)) {
          throw new Error('Invalid segment token payload');
        }

        return (tokens as TranscriptionToken[]).map((token) => ({
          ...token,
          startMs: segment.startMs + Math.max(0, token.startMs ?? 0),
          endMs:
            segment.startMs + Math.max(0, token.endMs ?? token.startMs ?? 0),
        }));
      }),
    )
      .then((segmentsTokens) => {
        if (cancelled) return;

        const mergedTokens = segmentsTokens
          .flat()
          .sort((a, b) =>
            a.startMs === b.startMs ? a.endMs - b.endMs : a.startMs - b.startMs,
          );

        setRemoteTokens(mergedTokens);
        loadedTokensSignatureRef.current = tokensDatasetSignature;
      })
      .catch((error) => {
        if (!cancelled) {
          setTokensError(
            error instanceof Error ? error.message : 'Failed to load tokens',
          );
          setRemoteTokens(null);
          loadedTokensSignatureRef.current = null;
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsTokensLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [playback?.playbackTimeline, shouldLoadTokens, tokensDatasetSignature]);

  useEffect(() => {
    if (!enabled) {
      setAudioTimeline(null);
      appliedTimelineSignatureRef.current = null;
      return;
    }

    if (playbackTimeline.length === 0) {
      setAudioTimeline(null);
      appliedTimelineSignatureRef.current = null;
      return;
    }

    if (appliedTimelineSignatureRef.current === playbackTimelineSignature) {
      return;
    }

    setAudioTimeline(playbackTimeline);
    appliedTimelineSignatureRef.current = playbackTimelineSignature;
  }, [enabled, playbackTimeline, playbackTimelineSignature, setAudioTimeline]);

  useEffect(() => {
    return () => {
      setAudioTimeline(null);
      appliedTimelineSignatureRef.current = null;
    };
  }, [setAudioTimeline]);

  const hasTokens = !!remoteTokens && remoteTokens.length > 0;
  const hasAudio = playbackTimeline.length > 0;
  const canHighlightTokens = hasTokens && hasAudio;
  const segmentBoundariesMs = useMemo(
    () => playbackTimeline.slice(1).map((segment) => segment.startMs),
    [playbackTimeline],
  );

  return {
    playback,
    playbackTimeline,
    segmentBoundariesMs,
    tokens: remoteTokens,
    isTokensLoading,
    tokensError,
    hasAudio,
    hasTokens,
    canHighlightTokens,
  };
};
